import { formatCurrency, formatNumber } from 'src/util'
import styles from './BenchmarkBasis.module.css'

type FrameworkType = 'ep-rates' | 'sor' | 'quantity'

type FrameworkLike = {
  name?: string
  type?: FrameworkType | null
}

type ChoiceLike = {
  _id: string
  name?: string
}

type BenchmarkBasisWizard = {
  specification: { [field: string]: string }
  details: {
    budget: number | ''
    quantity: number | ''
    location: string
  }
  sorType: string
  sorAdjustment: number | ''
  sorBudget: number | ''
  quantityInputs: { [choiceId: string]: number | '' }
  quantityBudget: number | ''
}

type BenchmarkBasisProps = {
  framework?: FrameworkLike | null
  wizard: BenchmarkBasisWizard
  choices?: ChoiceLike[] | null
}

// Resolve a human-readable label for a choice/field id. Falls back to the raw
// id when the choice isn't found in the loaded choices list (limitation noted
// in the backlog: the frontend only has access to whatever /choices returns).
function choiceLabel(choices: ChoiceLike[] | null | undefined, id: string): string {
  return (choices ?? []).find((c) => c._id === id)?.name ?? id
}

function Row(props: { label: string; value: string }) {
  return (
    <tr>
      <th scope="row" className={styles.label}>{props.label}</th>
      <td className={styles.value}>{props.value}</td>
    </tr>
  )
}

function BenchmarkBasis(props: BenchmarkBasisProps) {
  const { framework, wizard, choices } = props
  if (!framework) return null

  const type = framework.type ?? 'ep-rates'

  let rows: { label: string; value: string }[] = []

  if (type === 'quantity') {
    rows = Object.entries(wizard.quantityInputs ?? {})
      .filter(([, qty]) => qty !== '' && qty !== undefined && qty !== null)
      .map(([choiceId, qty]) => ({
        label: choiceLabel(choices, choiceId),
        value: formatNumber(Number(qty))
      }))
    if (wizard.quantityBudget !== '' && wizard.quantityBudget !== undefined) {
      rows.push({
        label: 'Annual Budget / Expenditure',
        value: formatCurrency(Number(wizard.quantityBudget))
      })
    }
  } else if (type === 'sor') {
    if (wizard.sorType) {
      rows.push({ label: 'Schedule of Rates', value: wizard.sorType })
    }
    if (wizard.sorBudget !== '' && wizard.sorBudget !== undefined) {
      rows.push({
        label: 'Annual Budget / Expenditure',
        value: formatCurrency(Number(wizard.sorBudget))
      })
    }
    if (wizard.sorAdjustment !== '' && wizard.sorAdjustment !== undefined) {
      rows.push({
        label: 'Current Adjustment',
        value: `${formatNumber(Number(wizard.sorAdjustment))}%`
      })
    }
  } else {
    // ep-rates: the user's selections (choices)
    rows = Object.entries(wizard.specification ?? {})
      .filter(([, value]) => value !== '' && value !== undefined && value !== null)
      .map(([fieldId, value]) => ({
        label: choiceLabel(choices, fieldId),
        value: String(value)
      }))
  }

  return (
    <div className={styles.container}>
      <div className={styles.title}>Basis of benchmark</div>
      <div className={styles.frameworkName}>{framework.name}</div>
      {rows.length > 0 ? (
        <table className={styles.table}>
          <tbody>
            {rows.map((row, index) => (
              <Row key={index} label={row.label} value={row.value} />
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  )
}

export { BenchmarkBasis }
export type { BenchmarkBasisWizard, FrameworkLike, ChoiceLike }
